import { z } from 'zod';

import { zodParsingCaseBaseSchema } from '../validations';

export const ARTIFACT_TYPE = [
  'RAW_FILE',
  'ORIGINAL_PDF_FILE',
  'OCR_JSON',
  'PARSED_JSON',
  'OUTGOING_DLN',
] as const;

/** Autocomplete + typo-safety on known values; still accepts any string. */
export type ArtifactType = (typeof ARTIFACT_TYPE)[number] | (string & {});

export const zodCaseArtifactResponseSchema = z.object({
  artifactType: z.string(),
  createdOn: z.iso.datetime({ offset: true }),
  dataHashSha256: z.hash('sha256'),
  sasUrl: z.string().nullish(),
});

export const zodCaseAttemptResponseSchema = z.object({
  artifacts: z.array(zodCaseArtifactResponseSchema).default([]),
  caseLinkage: z.string().nullish(),
  createdOn: z.iso.datetime({ offset: true }),
  hasParsedData: z.boolean().default(false),
  jobId: z.uuid(),
  jobStatus: z.string(),
  source: z.string().nullish(),
  statusDetails: z.record(z.string(), z.unknown()).nullish(),
  trigger: z.string().nullish(),
});

const zodValidationErrorFieldRefSchema = z.object({
  fieldId: z.string(),
  indices: z.array(z.number().int()).default([]),
});

/** One OCR validation finding; `msg` arrives already localized by the API. */
export const zodValidationErrorResponseSchema = z.object({
  fieldRefs: z.array(zodValidationErrorFieldRefSchema).default([]),
  msg: z.string(),
  severity: z.enum(['err', 'war', 'inf']),
  type: z.string(),
});

const zodModelIdSchema = z
  .union([
    z.string(),
    z.array(
      z.object({ modelId: z.string(), modelType: z.string().optional() }),
    ),
  ])
  .nullish()
  .transform((value) =>
    Array.isArray(value)
      ? value.length === 1
        ? (value[0]?.modelId ?? null)
        : null
      : value,
  );

export const zodParsingCaseDetailResponseSchema =
  zodParsingCaseBaseSchema.extend({
    attempts: z.array(zodCaseAttemptResponseSchema).default([]),
    chainId: z.uuid().nullish(),
    dlnData: z.record(z.string(), z.unknown()).nullish(),
    /** Where `dlnData` came from (informational). */
    dlnDataSource: z.string().nullish(),
    /**
     * Form state saved without a retry, exactly as the admin app stored it.
     * Only the form knows its shape, so it is validated where it is applied.
     */
    draft: z.record(z.string(), z.unknown()).nullish(),
    meta: z.record(z.string(), z.unknown()).nullish(),
    /**
     * The model that read the document: a plain id on OCR scans, the model
     * list on AI-format cases. Normalized to one id; a list of more or less
     * than one model names no single one.
     */
    modelId: zodModelIdSchema,
    /** The exact OCR document the parser will re-use on retry. */
    ocrJson: z.record(z.string(), z.unknown()).nullish(),
    parentCaseId: z.uuid().nullish(),
    /** Every parser format in the case's job chain (`ai`, `ocr`, …). */
    parserFormats: z.array(z.string()).nullish(),
    pdfSasUrl: z.string().nullish(),
    /** Capability flag: the parser for this case can apply line-item overrides. */
    supportsLineItemOverrides: z.boolean().default(false),
    validationErrors: z.array(zodValidationErrorResponseSchema).default([]),
  });
